import { CustomData, DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { AttachmentOpenAction, AttachmentPreviewMethod } from 'owa-attachment-data';
import { AttachmentFileType } from 'owa-attachment-file-types';
import { AttachmentFullViewState, calculateUseJsApi } from 'owa-attachment-full-data';
import {
    AttachmentClass,
    AttachmentConstants,
    AttachmentModel,
    lazyGetAttachment,
    lazyRefreshPreviewImageUrl,
    ReferenceAttachmentModel,
} from 'owa-attachment-model-store';
import getGDriveNonImagePreviewUrl from 'owa-attachment-model-store/lib/utils/getGDriveNonImagePreviewUrl';
import { isCloudyAttachmentType } from 'owa-attachment-model-store/lib/utils/isCloudyAttachment';
import {
    getAttachmentPolicyInfoForPublicComputer,
    getUserAttachmentPolicyChecker,
} from 'owa-attachment-policy';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import getAttachmentTextPreview, {
    GetAttachmentTextPreviewError,
} from 'owa-attachment-text-preview/lib/services/getAttachmentTextPreview';
import { getAttachmentUrl } from 'owa-attachment-url';
import getWacInfoForOneDriveJsApi from 'owa-attachment-wac/lib/services/getWacInfoForOneDriveJsApi';
import getWacUrlForAttachmentId, {
    GetWacUrlForAttachmentIdError,
} from 'owa-attachment-wac/lib/utils/getWacUrlForAttachmentId';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getExtensionWithoutDotFromFileName } from 'owa-file';
import fileExtensionToOfficeFileType from 'owa-file/lib/utils/fileExtensionToOfficeFileType';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import { getGuid } from 'owa-guid';
import loc, { isStringNullOrWhiteSpace } from 'owa-localize';
import { errorGetWacUrl } from 'owa-locstrings/lib/strings/errorgetwacurl.locstring.json';
import { errorGetWacUrlAttachmentDataProviderError } from 'owa-locstrings/lib/strings/errorgetwacurlattachmentdataprovidererror.locstring.json';
import { ICSFileErrorText } from 'owa-locstrings/lib/strings/icsfileerrortext.locstring.json';
import {
    findInlineComposeViewState,
    lazyCreateReply,
    lazyLoadDraftToCompose,
    lazyTrySaveAndCloseCompose,
} from 'owa-mail-compose-actions';
import { ComposeTarget } from 'owa-mail-compose-store';
import { InstrumentationContext, mailStore } from 'owa-mail-store';
import {
    AddToCalendarStatus,
    IcsCalendarEventType,
    IcsPreviewState,
    lazyGetIcsCalendarItemOrchestrator,
} from 'owa-preview-ics';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import GetAttachmentTextPreviewReadMode from 'owa-service/lib/contract/GetAttachmentTextPreviewReadMode';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import {
    EditMode,
    lazyEditAndReply,
    lazyEditInDraft,
    lazySxSStoreModule,
    onSxSChangeOrClose,
    PreviewPaneSelectionSource,
    SxSCustomization,
    sxsStore,
} from 'owa-sxs-store';
import { AutoHideEmailEvent, ImageGalleryItem, lazySxSDataModule, SxSViewState } from 'owa-sxsdata';
import { trace } from 'owa-trace';
import SxSReadingPaneStrategy from '../types/SxSReadingPaneStrategy';
import attachmentPreviewMethodToString from '../utils/attachmentPreviewMethodToString';
import getAttachmentPdfPreview from '../utils/getAttachmentPdfPreview';
import getCurrentReadingPaneId from '../utils/getCurrentReadingPaneId';
import insertSxSV2IntoDomHelper from '../utils/insertSxSV2IntoDomHelper';
import { isUserError, throwUserError } from '../utils/throwUserError';
import { initializeReadingPaneInSxS } from './internalActions';
import { AddToCalendarSizeLimitExceedsError } from './previewAttachmentInSxS.locstring.json';
import { loadItemAttachment } from './publicActions';

// used to determine if WAC preview should use high contrast mode
const powerPointFileExtensions = [
    '.odp',
    '.pot',
    '.potm',
    '.potx',
    '.pps',
    '.ppsm',
    '.ppsx',
    '.ppt',
    '.pptm',
    '.pptx',
];

async function loadDraftToCompose(itemId: ClientItemId) {
    const loadDraftToCompose = await lazyLoadDraftToCompose.import();
    loadDraftToCompose(itemId.Id, ComposeTarget.SxS);
}

async function createReply(referenceItemId: ClientItemId): Promise<ClientItemId> {
    const mailboxInfo = referenceItemId.mailboxInfo;
    const groupId =
        mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox
            ? mailboxInfo.mailboxSmtpAddress
            : null;
    const createReply = await lazyCreateReply.import();
    const message = await createReply(
        referenceItemId.Id,
        true, // isReplyAll
        groupId,
        // For SXS view we shall not override the server behavior where upon r/R/F it marks the reference item as read
        // which means this will be the one case where the item may get removed from unread filter but its still less disruptive as SxS view will be on top of LV.
        false /* suppressServerMarkReadOnReplyOrForward */
    );

    const itemId: ClientItemId = {
        ...message.ItemId,
        mailboxInfo: referenceItemId.mailboxInfo,
    };

    return itemId;
}

async function trySaveAndCloseCompose(conversationId: string): Promise<ClientItemId> {
    /* This is to check if we already created a reply by clicking Reply or Reply all button.
    If it has been created, we want to re-use the draft instead of creating a new reply */
    const composeViewState = findInlineComposeViewState(conversationId);

    if (composeViewState) {
        const trySaveAndCloseCompose = await lazyTrySaveAndCloseCompose.import();
        await trySaveAndCloseCompose(composeViewState);
        return composeViewState.itemId as ClientItemId;
    } else {
        return null;
    }
}

export default async function previewAttachmentInSxS(
    attachmentViewState: AttachmentFullViewState,
    parentItemId: ClientItemId,
    isDraft: boolean,
    isEdit: boolean,
    selectionSource: PreviewPaneSelectionSource,
    createImageGallery: () => Promise<ImageGalleryItem[]>,
    readingPaneStrategy: SxSReadingPaneStrategy,
    instrumentationContext: InstrumentationContext,
    customization: SxSCustomization = SxSCustomization.Mail
) {
    const clickTime = new Date();
    const sxsStoreModule = await lazySxSStoreModule.import();

    if (!insertSxSV2IntoDomHelper.available()) {
        // current module does not have 'owa-sxs-v2' imported
        // open a popout instead
        sxsStoreModule.openSxSInANewWindow(parentItemId, attachmentViewState.attachmentId.Id, null);
        return;
    }

    insertSxSV2IntoDomHelper.run();
    const sxsDataModule = await lazySxSDataModule.import();
    const { onPreviewOperationStart, onPreviewOperationFinished } = sxsStoreModule;

    const sxsViewState: SxSViewState = sxsStore.viewState;
    const getAttachment = await lazyGetAttachment.import();
    const attachmentModel = getAttachment(attachmentViewState.attachmentId);
    const strategy = attachmentViewState.strategy;
    const isDownloadOpenActionSupported =
        strategy.supportedOpenActions.indexOf(AttachmentOpenAction.Download) !== -1;
    const downloadUrl = isDownloadOpenActionSupported ? attachmentModel.download.url : null;
    const isSaveToCloudSupported =
        strategy.isSaveToCloudSupported &&
        isStringNullOrWhiteSpace(attachmentModel.model.AttachmentOriginalUrl);
    const saveToCloudStatus = isSaveToCloudSupported
        ? SaveToCloudStatus.available
        : SaveToCloudStatus.disabled;
    const optionalParameters = {
        downloadUrl: downloadUrl,
        downloadShouldOpenNewTab: false,
        fileName: attachmentModel.model.Name,
        useHighContrastCommandBar: false,
        id: attachmentViewState.attachmentId,
        isCloudy: attachmentViewState.isCloudy,
    };

    // change Native PDF viewer to PDF.js if the parent item is IRM protected
    // currently we don't have IRM information from the strategy
    // this is a temporary solution until
    // WI #59615 (Refactor AttachmentViewStrategy to be dynamic instead of statically computed value)
    // is done
    let previewMethod = strategy.previewMethod;
    let isParentItemIrmProtected = false;
    if (
        strategy.previewMethod === AttachmentPreviewMethod.NativeView ||
        strategy.previewMethod === AttachmentPreviewMethod.PdfJs
    ) {
        const parentItem = mailStore.items.get(parentItemId.Id);
        if (parentItem && !!parentItem.RightsManagementLicenseData) {
            previewMethod = AttachmentPreviewMethod.PdfJs;
            isParentItemIrmProtected = true;
        }
    }

    const { perfDatapoint, perfDatapointCreatedTime } = logDatapoint(
        attachmentModel,
        previewMethod,
        attachmentViewState.isCloudy,
        sxsStore.editState.mode !== EditMode.View,
        selectionSource,
        attachmentViewState.fileType,
        sxsStoreModule.repeatTracker.contains(attachmentViewState.attachmentId.Id),
        !sxsStore.shouldShow && getCurrentReadingPaneId() // only pass in reading pane Id when SxS was not showing
    );
    const autoHideEventHandler = () => {
        perfDatapoint.addCustomData({
            autoHide: true,
        });
    };
    sxsViewState.eventManager.on(AutoHideEmailEvent, autoHideEventHandler);

    const currentPendingOperation = {
        id: `${new Date().getTime()}_${Math.floor(Math.random() * 1000)}`,
        attachmentId: attachmentViewState.attachmentId,
    };
    onPreviewOperationStart(currentPendingOperation);

    try {
        // wait if in edit mode
        const success = await sxsStoreModule.canCloseSxS();

        if (!success) {
            // synchronize attachment did not return successfully
            // adding custom data to the datapoint
            perfDatapoint.addCustomData({
                exitEditMode: 'notSaved',
            });
        }

        throwIfPreviewOperationNotCurrent();
        if (!isEdit || isDraft) {
            initializeReadingPaneInSxS(
                readingPaneStrategy,
                selectionSource,
                parentItemId,
                instrumentationContext
            );
        }

        sxsDataModule.setPreviewLoading(sxsViewState, '', optionalParameters);
        sxsStoreModule.showSxS(
            sxsStore.shouldShow ? null : customization,
            {
                previewMode: previewMethod,
                attachmentId: attachmentViewState.attachmentId,
                selectionSource: selectionSource,
                isCloudy: attachmentViewState.isCloudy,
                saveToCloudStatus,
                textPreviewReadMode: GetAttachmentTextPreviewReadMode.AutoDetect,
            },
            {
                editAllowed: strategy.allowEdit,
                itemId: parentItemId,
                isDraft: isDraft,
            },
            null // selectedLinkId
        );

        onSxSChangeOrClose(null);
        let loadedTime;
        if (!isEdit) {
            switch (previewMethod) {
                case AttachmentPreviewMethod.Image:
                    await handleImagePreview();
                    break;
                case AttachmentPreviewMethod.Wac:
                    await handleWacPreview();
                    break;
                case AttachmentPreviewMethod.GoogleDoc:
                    handleGoogleDocPreview();
                    break;
                case AttachmentPreviewMethod.NativeView:
                    await handleNativeViewPreview();
                    break;
                case AttachmentPreviewMethod.PdfJs:
                    await handlePdfJsPreview();
                    break;
                case AttachmentPreviewMethod.Audio:
                    handleAudioPreview();
                    break;
                case AttachmentPreviewMethod.Video:
                    handleVideoPreview();
                    break;
                case AttachmentPreviewMethod.Text:
                    await handleTextPreview();
                    break;
                case AttachmentPreviewMethod.ItemAttachment:
                    handleItemAttachmentPreview();
                    break;
                case AttachmentPreviewMethod.CalendarEvent:
                    await handleIcsPreview();
                    break;
                default:
                    throw new Error('Unsupported preview method');
            }

            loadedTime = await sxsStoreModule.waitForPreviewLoadOrThrowError();
        }
        throwIfPreviewOperationNotCurrent();
        onPreviewOperationFinished();

        if (isEdit) {
            if (isDraft) {
                const editInDraft = await lazyEditInDraft.import();
                editInDraft(loadDraftToCompose);
            } else {
                const editAndReply = await lazyEditAndReply.import();
                editAndReply(loadDraftToCompose, createReply, trySaveAndCloseCompose);
            }
        }

        if (!loadedTime) {
            perfDatapoint.end();
        } else {
            const duration = loadedTime - perfDatapointCreatedTime;
            perfDatapoint.end(duration);
        }

        sxsStoreModule.repeatTracker.add(attachmentViewState.attachmentId.Id);
    } catch (error) {
        perfDatapoint.endWithError(
            isUserError(error) ? DatapointStatus.UserError : DatapointStatus.ServerError,
            error
        );
    } finally {
        sxsViewState.eventManager.off(AutoHideEmailEvent, autoHideEventHandler);
    }

    /***************** Below are local functions *****************/
    async function handleImagePreview() {
        const imageGallery = !createImageGallery ? null : await createImageGallery();
        throwIfPreviewOperationNotCurrent();
        sxsDataModule.setImagePreviewState(
            sxsViewState,
            attachmentModel.previewImage.url,
            imageGallery,
            optionalParameters
        );
        const refreshPreviewImageUrl = await lazyRefreshPreviewImageUrl.import();
        await refreshPreviewImageUrl(
            attachmentViewState.attachmentId,
            attachmentViewState.isReadOnly
        );
        throwIfPreviewOperationNotCurrent();
        sxsDataModule.setImagePreviewState(
            sxsViewState,
            getAttachment(attachmentViewState.attachmentId).previewImage.url,
            imageGallery,
            optionalParameters
        );
    }

    async function handleWacPreview() {
        const providerType: string = (attachmentModel.model as ReferenceAttachment).ProviderType;
        const useJsApi: boolean = calculateUseJsApi(
            attachmentViewState.isCloudy,
            providerType,
            attachmentModel.model.Name
        );
        if (useJsApi && providerType === AttachmentConstants.providers.ONE_DRIVE_PRO) {
            await handleWacPreviewByVroom();
        } else {
            await handleWacPreviewByOws();
        }
    }

    async function handleWacPreviewByOws() {
        try {
            const isEdit = false;
            const wacUrl = await getWacUrlForAttachmentId(
                attachmentViewState.attachmentId,
                isEdit,
                PreviewPaneSelectionSource[selectionSource]
            );

            // use high contrast command bar if it is a PowerPoint file
            if (attachmentModel.model.Name) {
                const extIndex = attachmentModel.model.Name.lastIndexOf('.');
                if (extIndex >= 0) {
                    const extension = attachmentModel.model.Name.substr(extIndex).toLowerCase();
                    optionalParameters.useHighContrastCommandBar =
                        powerPointFileExtensions.indexOf(extension) >= 0;
                }
            }

            throwIfPreviewOperationNotCurrent();
            sxsDataModule.setWacPreviewState(
                sxsViewState,
                wacUrl.wacUrl, // wacUrl
                null, // JsApiInfo
                wacUrl.accessToken,
                wacUrl.accessTokenTtl,
                wacUrl.additionalParameters,
                wacUrl.deprecatedTokenAndParameters, // to be removed soon
                clickTime.getTime(),
                !isEdit, // readOnly info
                optionalParameters
            );
        } catch (error) {
            const typedError = error as GetWacUrlForAttachmentIdError;
            if (typedError?.errorMessage) {
                trace.warn(
                    `Something went wrong while getting the attachment state: ${typedError}`
                );
                sxsDataModule.setPreviewError(sxsViewState, typedError.errorMessage);
                throw typedError.error;
            }

            throw error;
        }
    }

    async function handleWacPreviewByVroom() {
        try {
            perfDatapoint.addCustomData({
                useJsApi: 'true',
            });

            const openUrl: string = (attachmentModel as ReferenceAttachmentModel).openUrl;
            if (openUrl == null) {
                throw new Error('openUrl is null');
            }

            // JsAPI should always open file in Edit mode.
            // However Excel app cannot handle legacy format properly. As a temporary workaround,
            // XLS file will be opened in ReadOnly mode.
            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/49074
            const fileName = attachmentModel.model.Name;
            const extension = fileName && fileName.substring(fileName.lastIndexOf('.'));
            const isEdit = extension !== '.xls';

            const apiResponse = await getWacInfoForOneDriveJsApi(
                AttachmentDataProviderType.OneDrivePro,
                openUrl,
                isEdit,
                true /* This is triggered by user clicking. Set redeemSharingLinkIfNecessary to true */
            );

            if (!apiResponse) {
                throw new Error('Vroom API call failed');
            }

            throwIfPreviewOperationNotCurrent();

            const owaSessionId = getGuid();

            sxsDataModule.setWacPreviewState(
                sxsViewState,
                apiResponse.openWith.wac.applicationUrl, // wacUrl
                {
                    bootScriptUrl: apiResponse.openWith.wac.bootstrapperUrl,
                    wopiSrcUrl: apiResponse.openWith.wac.wopiSrc,
                    accessToken: apiResponse.openWith.wac.accessToken,
                    accessTokenExpiry: apiResponse.openWith.wac.accessTokenExpiry,
                    fileSize: attachmentModel.model.Size,
                    fileGetUrl: apiResponse.openWith.wac.fileGetUrl,
                    bundleInfo: apiResponse.officeBundle,
                    fetchStartTime: apiResponse.fetchStartTime,
                    responseStartTime: apiResponse.responseStartTime,
                    openUrl: openUrl,
                    owaSessionId: owaSessionId,
                },
                null, // accessToken,
                null, // accessTokenTtl,
                null, // additionalParameters,
                null, // onedrive token
                clickTime.getTime(),
                apiResponse.currentUserRole.readOnly,
                optionalParameters
            );

            perfDatapoint.addCustomData({
                owaSessionId: owaSessionId,
            });
        } catch (error) {
            trace.warn(`Something went wrong while getting the attachment state: ${error.message}`);
            sxsDataModule.setPreviewError(sxsViewState, loc(errorGetWacUrl));
            throw error;
        }
    }

    function handleGoogleDocPreview() {
        sxsDataModule.setIframePreviewState(
            sxsViewState,
            (attachmentModel.model as ReferenceAttachment).AttachLongPathName,
            optionalParameters
        );
    }

    async function handleNativeViewPreview() {
        if (
            isCloudyAttachmentType(attachmentModel.model) &&
            attachmentModel.model.ProviderType === AttachmentConstants.providers.GDRIVE
        ) {
            await handleNativeViewPreviewForGDrive();
        } else {
            const attachmentPrintUrl = getAttachmentUrl(
                attachmentViewState.attachmentId,
                attachmentModel.model,
                AttachmentUrlType.Print,
                isCloudyAttachmentType(attachmentModel.model),
                attachmentViewState.isReadOnly
            );
            sxsDataModule.setNativePdfPreviewState(
                sxsViewState,
                attachmentModel.download.url + '&isDocumentPreview=True#toolbar=0',
                attachmentPrintUrl,
                'application/pdf',
                optionalParameters
            );
        }
    }

    async function handleNativeViewPreviewForGDrive() {
        let caughtError: Error = null;
        try {
            const url = await getGDriveNonImagePreviewUrl(
                attachmentModel.model as ReferenceAttachment
            );
            if (url) {
                throwIfPreviewOperationNotCurrent();
                sxsDataModule.setNativePdfPreviewState(
                    sxsViewState,
                    url,
                    null,
                    'application/pdf',
                    optionalParameters
                );
                return;
            }
        } catch (error) {
            caughtError = error;
        }

        throwIfPreviewOperationNotCurrent();
        sxsDataModule.setPreviewError(sxsViewState, loc(errorGetWacUrlAttachmentDataProviderError));
        throw caughtError || new Error('Unexpected error in handleNativeViewPreviewForGDrive');
    }

    async function handlePdfJsPreview() {
        const allowDownload = attachmentModel.info.allowDownload;
        const publicPolicy = getAttachmentPolicyInfoForPublicComputer(attachmentModel.model, true);
        const usePreviewUrl =
            allowDownload && publicPolicy.directFileAccessEnabled && !isParentItemIrmProtected;
        if (usePreviewUrl) {
            sxsDataModule.setPdfJsPreviewState(
                sxsViewState,
                attachmentModel.download.url + '&isDocumentPreview=True',
                false, // printDisabled
                optionalParameters
            );
        } else {
            const data = await getAttachmentPdfPreview(attachmentViewState.attachmentId);
            throwIfPreviewOperationNotCurrent();
            sxsDataModule.setPdfJsPreviewStateByData(
                sxsViewState,
                data.data,
                (!allowDownload && !getUserAttachmentPolicyChecker().printWithoutDownloadEnabled) ||
                    !data.printAllowed, // printDisabled
                data.copyAllowed,
                data.exportAllowed,
                optionalParameters
            );
        }
    }

    function handleAudioPreview() {
        sxsDataModule.setAudioPreviewState(
            sxsViewState,
            attachmentModel.download.url,
            optionalParameters
        );
    }

    function handleVideoPreview() {
        sxsDataModule.setVideoPreviewState(
            sxsViewState,
            attachmentModel.download.url,
            optionalParameters
        );
    }

    async function handleTextPreview() {
        try {
            const textContent = await getAttachmentTextPreview(
                attachmentViewState.attachmentId.Id,
                attachmentViewState.attachmentId.mailboxInfo?.requestOptions,
                GetAttachmentTextPreviewReadMode.AutoDetect,
                PreviewPaneSelectionSource[selectionSource]
            );
            throwIfPreviewOperationNotCurrent();
            sxsDataModule.setTextPreviewState(sxsViewState, textContent, optionalParameters);
        } catch (error) {
            const typedError: GetAttachmentTextPreviewError = error;
            if (typedError?.errorMessage) {
                throwIfPreviewOperationNotCurrent();
                sxsDataModule.setPreviewError(sxsViewState, typedError.errorMessage);
                throw typedError.error;
            }

            throw error;
        }
    }

    function handleItemAttachmentPreview() {
        loadItemAttachment(attachmentViewState.attachmentId, null);
        sxsDataModule.setCustomPreviewState(sxsViewState, optionalParameters);
    }

    async function handleIcsPreview() {
        const icsPreviewState: IcsPreviewState = {
            calendarItem: null,
            isIcsAttachmentLoading: true,
            icsCalendarEventType: IcsCalendarEventType.UNKNOWN,
            attachmentId: attachmentViewState.attachmentId,
            addToCalendarStatus: AddToCalendarStatus.available,
            errorMessage: null,
        };
        const getIcsCalendarItem = await lazyGetIcsCalendarItemOrchestrator.import();
        let icsError: string = null;
        await getIcsCalendarItem({
            attachmentId: attachmentViewState.attachmentId,
            size: attachmentModel.model.Size,
            showSizeLimitExceedsError: () => {
                icsError = loc(AddToCalendarSizeLimitExceedsError);
            },
            errorCallback: () => {
                icsError = loc(ICSFileErrorText);
            },
            state: icsPreviewState,
        });
        throwIfPreviewOperationNotCurrent();
        if (!icsError) {
            sxsStoreModule.onShowCustomPreview(icsPreviewState);
            sxsDataModule.setCustomPreviewState(sxsViewState, optionalParameters);
        } else {
            sxsDataModule.setPreviewError(sxsViewState, icsError);
            throw new Error(icsError);
        }
    }

    function throwIfPreviewOperationNotCurrent() {
        if (
            !sxsStore.pendingPreviewOperation ||
            currentPendingOperation.id !== sxsStore.pendingPreviewOperation.id
        ) {
            throwUserError('Preview changed before loading finished');
        }
    }
}

const datapointTimeoutOverride = 3 * 60 * 1000; // 3 minutes
let previousReadingPaneId = null;

function logDatapoint(
    attachment: AttachmentModel,
    previewMethod: AttachmentPreviewMethod,
    isCloudy: boolean,
    isChangingFromEditing: boolean,
    actionSource: PreviewPaneSelectionSource,
    attachmentFileType: AttachmentFileType,
    isRepeat: boolean,
    currentReadingPaneId: string
): { perfDatapoint: PerformanceDatapoint; perfDatapointCreatedTime: number } {
    const fileType =
        (attachment.attachmentClass !== AttachmentClass.ItemAttachment &&
            attachment.attachmentClass !== AttachmentClass.ItemIdAttachment &&
            getExtensionWithoutDotFromFileName(attachment.model.Name || '')) ||
        attachment.model.ContentType ||
        '';
    const perfDatapoint = new PerformanceDatapoint('PreviewAttachmentInSxSE2E', {
        isQos: true,
        timeout: datapointTimeoutOverride,
    });
    const perfDatapointCreatedTime = Date.now();
    let fromSameReadingPane = false;
    if (!!currentReadingPaneId) {
        fromSameReadingPane =
            !!previousReadingPaneId && previousReadingPaneId === currentReadingPaneId;
        previousReadingPaneId = currentReadingPaneId;
    }

    const customData: CustomData = {
        previewMethod: attachmentPreviewMethodToString(previewMethod),
        isCloudy: isCloudy ? 'cloudy' : 'classic',
        isChangingFromEditing: isChangingFromEditing ? 'changingFromEdit' : '',
        officeFileType: fileExtensionToOfficeFileType(fileType),
        actionSource: PreviewPaneSelectionSource[actionSource],
        attachmentFileType,
        isRepeat,
        downloadAllowed: !!attachment.download.url,
        fromSameReadingPane,
    };

    let associatedFlights = '';
    if (isFeatureEnabled('doc-SxS-jsApi-ODB-Base')) {
        associatedFlights += 'doc-SxS-jsApi-ODB-Base;';
    }
    if (isFeatureEnabled('doc-SxS-jsApi-ODB-BaseNoEditString')) {
        associatedFlights += 'doc-SxS-jsApi-ODB-BaseNoEditString;';
    }
    if (isFeatureEnabled('doc-SxS-jsApi-ODB-Readonly')) {
        associatedFlights += 'doc-SxS-jsApi-ODB-Readonly;';
    }
    if (isFeatureEnabled('doc-SxS-jsApi-ODB-Basecf')) {
        associatedFlights += 'doc-SxS-jsApi-ODB-Basecf;';
    }

    customData['associatedFlights'] = associatedFlights;

    customData['hostName'] = window.location.hostname;

    // there are a few custom data that are added later
    // they are 'useJsApi' and 'autoHide'
    perfDatapoint.addCustomData(customData);
    perfDatapoint.addCosmosOnlyData(
        JSON.stringify({
            attachmentId: attachment?.id?.Id,
            fileType: fileType?.substring(0, 4) || '',
            fileSize: attachment.model.Size,
        })
    );

    // TODO: VSO #60905: Remove datapoint after validating telemetry compared with PreviewAttachmentInSxSE2E
    logUsage('PreviewAttachmentInSxSUsage', customData);
    return { perfDatapoint, perfDatapointCreatedTime };
}
